import { fetchAgg } from '../../../../common/utils/fetch-agg';
import {
	type CacheAnalyticsCallback,
	type NetworkCallbacks,
	promiseCache,
} from '../../../../utils/caching/persist-refresh';
import { SEARCH_COMBINED_CONFIG_CACHE_KEY } from '../../../../utils/caching/persist-refresh/constants';
import { DegradablePromise, type DegradationCallback } from '../../../utils/degradable-promise';

import { generateCombinedConfigHash as getCombinedConfigHash } from './hash';
import { getSearchConfigQuery, GQL_OPERATION_NAME } from './query';

export const SEARCH_COMBINED_CONFIG: "search-combined-config" = SEARCH_COMBINED_CONFIG_CACHE_KEY;

type CombinedConfigQueryVariables = {
	cloudId: string;
};

export type SearchCombinedFirstPartyProduct = {
	name: string;
};

export type SearchCombinedThirdPartyProduct = {
	name: string;
	outboundAuthUrl: string;
	isUserOAuthed: boolean;
	providerId?: string | null;
	integrationARI?: string | null;
	workspaceName?: string | null;
	workspaceUrl?: string | null;
	datasourceId?: string | null;
	connectionId?: string | null;
	connectorSyncType?: string | null;
	isImpersonationAuthEnabled?: boolean | null;
	connectorCreatedAt?: string | null;
};

export type SearchCombinedSiteProductInfo = {
	name?: string | null;
	dataRegion?: string[] | string | null;
};

export type SearchCombinedSiteMetadata = {
	isRovoEnabled?: boolean;
	isJiraIssueIndexed?: boolean;
	applicationMode?: string | null;
	siteName?: string | null;
	orgId?: string | null;
	productEdition?: string | null;
	productInfo?: SearchCombinedSiteProductInfo[] | null;
};

export type SearchCombinedConfigPayload = {
	firstPartyProducts?: SearchCombinedFirstPartyProduct[];
	thirdPartyProducts?: SearchCombinedThirdPartyProduct[];
	intercomHmac?: string | null;
	siteMetadata?: SearchCombinedSiteMetadata | null;
};

type SearchCombinedConfigResponse = {
	search?: {
		configuration?: SearchCombinedConfigPayload | null;
	} | null;
};

export type CombinedSearchConfig = SearchCombinedConfigPayload;

export type CombinedSearchConfigProps = {
	cloudId: string;
	userId?: string | null;
	aggAbsoluteUrl?: string;
	onlyRefreshIfExpired?: boolean;
	expiration?: number;
	degradationCallback?: DegradationCallback;
	cacheAnalyticsCallback?: CacheAnalyticsCallback;
	returnExpired?: boolean;
	networkCallbacks?: NetworkCallbacks;
	headers?: Record<string, string>;
};

const {
	refreshOnAccessDecorator: refreshOnAccessDecoratorCombined,
	update: updateCombinedCache,
	seed: seedCombinedCache,
} = promiseCache<SearchCombinedConfigPayload>({
	name: SEARCH_COMBINED_CONFIG,
});

export const updateCombinedConfigCacheEntry = (
	userId: string,
	cloudId: string,
	updater: (config: SearchCombinedConfigPayload) => SearchCombinedConfigPayload,
): Promise<boolean> => {
	const key = getCombinedConfigHash(userId, cloudId);
	return updateCombinedCache(key.toString(), updater);
};

export const seedCombinedConfigCacheEntry = ({
	userId,
	cloudId,
	config,
	expiration,
}: {
	userId?: string | null;
	cloudId: string;
	config: SearchCombinedConfigPayload;
	expiration?: number;
}): Promise<void> => {
	const key = getCombinedConfigHash(userId || '', cloudId);
	return seedCombinedCache(key.toString(), config, expiration);
};

export const getCombinedSearchConfig = async ({
	cloudId,
	userId,
	aggAbsoluteUrl,
	onlyRefreshIfExpired,
	expiration,
	degradationCallback = () => {},
	cacheAnalyticsCallback,
	returnExpired,
	networkCallbacks,
	headers,
}: CombinedSearchConfigProps): Promise<SearchCombinedConfigPayload> => {
	const key = getCombinedConfigHash(userId || '', cloudId);

	return refreshOnAccessDecoratorCombined({
		key: key.toString(),
		supplier: () => {
			const combinedConfigPromise = fetchAgg<
				CombinedConfigQueryVariables,
				SearchCombinedConfigResponse
			>({
				graphQuery: getSearchConfigQuery(),
				variables: { cloudId },
				operationName: GQL_OPERATION_NAME,
				aggAbsoluteUrl,
				headers,
			}).then((response) => {
				const hasErrors = response.errors?.some((error) => error);
				if (hasErrors) {
					const errorsAsString = response.errors?.map((error) => error.message).join(', ');
					throw new Error(`Search combined config response had errors: ${errorsAsString}`);
				}

				const configuration = response.data?.search?.configuration;
				if (!configuration) {
					throw new Error('Search combined config response missing configuration.');
				}

				return configuration;
			});

			const degrader = new DegradablePromise<SearchCombinedConfigPayload>(
				combinedConfigPromise,
				degradationCallback,
			);

			return degrader.promise();
		},
		onlyRefreshIfExpired,
		expiration,
		returnExpired,
		analyticsCallback: cacheAnalyticsCallback,
		networkCallbacks,
		serveResolvedDuringRefresh: true,
	});
};
